import type { AutomationRequestFrequency } from '@adweb/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { apiClient } from '../../lib/api-client';

import { FREQUENCY_OPTIONS } from './request-labels';

function apiErrorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: string } } } | null)?.response?.data;
  return data?.message ?? 'La demande n’a pas pu être envoyée. Réessayez dans un instant.';
}

const FIELD =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30';

/** Formulaire « Demander une automatisation » (tout utilisateur, pour son organisation). */
export function RequestAutomationModal({
  open,
  onClose,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [frequency, setFrequency] = useState<AutomationRequestFrequency>('WEEKLY');
  const [tools, setTools] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setDescription('');
    setFrequency('WEEKLY');
    setTools('');
    setError(null);
  }, [open]);

  const send = useMutation({
    mutationFn: () =>
      apiClient.post('/automation-requests', {
        title,
        description,
        frequency,
        ...(tools.trim() ? { tools } : {}),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['automation-requests'] });
      onSent?.();
      onClose();
    },
    onError: (err) => setError(apiErrorMessage(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    send.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Demander une automatisation"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Annuler
          </Button>
          <Button form="request-automation-form" type="submit" disabled={send.isPending}>
            {send.isPending ? 'Envoi…' : 'Envoyer la demande'}
          </Button>
        </>
      }
    >
      <form id="request-automation-form" onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-600">
          Décrivez la tâche que vous aimeriez automatiser. Adweb-Systèmes reçoit votre demande et
          vous tient informé de son avancement ici et par e-mail.
        </p>
        <div>
          <label htmlFor="req-title" className="mb-1 block text-xs font-medium text-slate-700">
            Titre <span className="text-red-600">*</span>
          </label>
          <input
            id="req-title"
            required
            minLength={3}
            maxLength={120}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ex. Relance des devis non signés"
            className={FIELD}
          />
        </div>
        <div>
          <label
            htmlFor="req-description"
            className="mb-1 block text-xs font-medium text-slate-700"
          >
            Votre besoin <span className="text-red-600">*</span>
          </label>
          <textarea
            id="req-description"
            required
            minLength={10}
            maxLength={4000}
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ce que vous faites aujourd’hui à la main, ce que vous aimeriez obtenir, le temps que ça vous prend…"
            className={FIELD}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="req-frequency"
              className="mb-1 block text-xs font-medium text-slate-700"
            >
              Fréquence <span className="text-red-600">*</span>
            </label>
            <select
              id="req-frequency"
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as AutomationRequestFrequency)}
              className={FIELD}
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="req-tools" className="mb-1 block text-xs font-medium text-slate-700">
              Outils concernés
            </label>
            <input
              id="req-tools"
              maxLength={300}
              value={tools}
              onChange={(e) => setTools(e.target.value)}
              placeholder="Ex. Gmail, Excel, votre CRM"
              className={FIELD}
            />
          </div>
        </div>
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
          >
            {error}
          </p>
        )}
      </form>
    </Modal>
  );
}
